import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';

import TransitionScreen from '@/components/transitionScreen';
import { setInquiryDraftV2 } from '@/services/setInquiryDraftV2';
import { loadPendingInquiryDraft, clearPendingInquiryDraft } from '@/util/pendingInquiryDraft';
import { isMeaningfulData } from '@/utils/inquiryHelpers';

/**
 * Inquiry Redirect Handler
 *
 * This page handles the post-login flow for logged-out users who filled out the inquiry form.
 * It runs in the (secure) route group, so it requires authentication.
 *
 * Flow:
 * 1. Check local storage for pending inquiry draft data
 * 2. If found: create server draft via API, get draftId, clear local storage
 * 3. Redirect to /inquiry?draftId=xxx (or /inquiry if no pending data)
 */
export default function InquiryRedirect() {
  const router = useRouter();

  const hasProcessedRef = useRef(false);

  const createDraftMutation = useMutation({
    mutationFn: setInquiryDraftV2,
  });

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }

    hasProcessedRef.current = true;

    const handleRedirect = async () => {
      try {
        const pendingDraft = await loadPendingInquiryDraft();

        if (!pendingDraft) {
          router.replace('/inquiry');

          return;
        }

        const hasMeaningfulData = isMeaningfulData(pendingDraft);

        if (!hasMeaningfulData) {
          await clearPendingInquiryDraft();

          router.replace('/inquiry');

          return;
        }

        const serverDraft = await createDraftMutation.mutateAsync({
          cancerFocusId: pendingDraft.cancerFocusId,
          message: pendingDraft.message,
          noPII: pendingDraft.noPII,
          anonymous: pendingDraft.anonymous,
          expertIds: null,
          expertId: null,
        });

        await clearPendingInquiryDraft();

        if (serverDraft?.uuid) {
          router.replace(`/inquiry?draftId=${serverDraft.uuid}`);
        } else {
          router.replace('/inquiry');
        }
      } catch (error) {
        console.error('Failed to create draft from pending data:', error);

        await clearPendingInquiryDraft();

        router.replace('/inquiry');
      }
    };

    handleRedirect();
  }, [router, createDraftMutation]);

  return <TransitionScreen message="Loading your inquiry..." />;
}
